import Card from '../card/Card';
import H from '../texts/h/H';
import P from '../texts/p/P';
import Button from '../button/Button';
import TableSkeleton from '../TableSkeleton/TableSkeleton';
import GeneralSection from '../../organisms/MonetizationNew/GeneralSection';
import LoanSection from '../../organisms/MonetizationNew/LoanSection';
import CheckSection from '../../organisms/MonetizationNew/CheckSection';
import PromissoryNoteSection from '../../organisms/MonetizationNew/PromissoryNoteSection';
import CommercialEndorsementsSection from '../../organisms/MonetizationNew/CommercialEndorsementsSection';
import ObservationSection from '../../organisms/MonetizationNew/ObservationSection';
import LoanExecutionSection from '../../organisms/MonetizationNew/LoanExecutionSection';
import CheckPromissoryExecutionSection from '../../organisms/MonetizationNew/CheckPromissoryExecutionSection';
import { useMonetizationForm } from '../../hooks/useMonetizationForm';

interface MonetizationFormProps {
  mode: 'create' | 'edit';
  id?: string;
  operationType?: string;
  saveBeforeExecute?: boolean;
}

const MonetizationForm = ({ mode, id, operationType, saveBeforeExecute = false }: MonetizationFormProps) => {
  const {
    formData,
    formValues,
    loading,
    submitting,
    error,
    validationError,
    updateFormValues,
    handleGoBack,
    handleSubmit,
    handleSave,
    handleExecute,
    isFormValid,
    isExecuteValid,
    installments,
    clearing,
    isExecuted,
  } = useMonetizationForm({ mode, id, operationType });

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={8} columns={4} />
        </Card>
      </Card>
    );
  }

  if (error || !formData) {
    return (
      <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <H type="1" weight="regular" modifier="weak">{error || 'Datos no encontrados'}</H>
        </Card>
      </Card>
    );
  }

  const renderConditionalSection = () => {
    const operationType = formData.product_line_label;
    
    if (operationType === 'CDP Propios' || operationType === 'CDP Terceros') {
      return (
        <CheckSection 
          formData={formData}
          formValues={formValues}
          updateFormValues={updateFormValues}
        />
      );
    }
    
    if (operationType === 'Pagaré Dólar Link' || operationType === 'Pagaré en Pesos') {
      return (
        <PromissoryNoteSection 
          formData={formData}
          formValues={formValues}
          updateFormValues={updateFormValues}
        />
      );
    }
    
    if (operationType === 'Aval Comercial') {
      return (
        <CommercialEndorsementsSection 
          formData={formData}
          formValues={formValues}
          updateFormValues={updateFormValues}
        />
      );
    }
    
    switch (operationType) {
      case 'Préstamo':
        return (
          <LoanSection 
            formData={formData}
            formValues={formValues}
            updateFormValues={updateFormValues}
            operationType={operationType}
          />
        );
      case 'Operaciones Públicas':
        return (
          <LoanSection 
            formData={formData}
            formValues={formValues}
            updateFormValues={updateFormValues}
            operationType={operationType}
          />
        );
      case 'Cheque':
        return (
          <CheckSection 
            formData={formData}
            formValues={formValues}
            updateFormValues={updateFormValues}
          />
        );
      case 'Pagaré':
        return (
          <PromissoryNoteSection 
            formData={formData}
            formValues={formValues}
            updateFormValues={updateFormValues}
          />
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    return mode === 'create' ? 'Nueva monetización' : `Pendiente de ejecución nº ${formValues.id_global?.toString()}`;
  };

  const renderExecutionSection = () => {
    const operationType = formData.product_line_label;
    
    // For loans
    if (operationType === 'Préstamo' || operationType === 'Operaciones Públicas') {
      return (
        <LoanExecutionSection
          formValues={formValues}
          updateFormValues={updateFormValues}
          installments={installments}
          idGlobal={formValues.id_global?.toString()}
          isPublicOperation={operationType === 'Operaciones Públicas'}
        />
      );
    }
    
    // For checks
    if (operationType === 'CDP Propios' || operationType === 'CDP Terceros' || operationType === 'Cheque') {
      return (
        <CheckPromissoryExecutionSection
          formValues={formValues}
          updateFormValues={updateFormValues}
          clearing={clearing || 0}
          isCheck={true}
        />
      );
    }
    
    // For promissory notes
    if (operationType === 'Pagaré Dólar Link' || operationType === 'Pagaré en Pesos' || operationType === 'Pagaré') {
      return (
        <CheckPromissoryExecutionSection
          formValues={formValues}
          updateFormValues={updateFormValues}
          clearing={clearing ?? undefined}
          isCheck={false}
        />
      );
    }
    
    // For commercial endorsements
    if (operationType === 'Aval Comercial') {
      return (
        <CheckPromissoryExecutionSection
          formValues={formValues}
          updateFormValues={updateFormValues}
          clearing={clearing ?? undefined}
          isCheck={false}
          isCommercialEndorsement={true}
        />
      );
    }
    
    return null;
  };

  const renderActionButtons = () => {
    if (mode === 'create') {
      const isLoan = formData?.product_line_label === 'Préstamo';
      const isPublicOperation = formData?.product_line_label === 'Operaciones Públicas';
      const buttonText = isLoan 
        ? 'Confirmar creación y descargar certificado de garantía' 
        : 'Crear monetización';
      
      return (
        <Button 
          variant='primary'
          onClick={handleSubmit}
          disabled={submitting || !isFormValid()}
        >
          {submitting ? 'Creando...' : buttonText}
        </Button>
      );
    }

    // Edit mode buttons
    return (
      <>
        <Button 
          variant='contained'
          onClick={handleSave}
          disabled={submitting}
        >
          Guardar
        </Button>
        <Button 
          variant='primary'
          onClick={async () => {
            if (isExecuted) return;
            if (saveBeforeExecute) {
              const saved = await handleSave();
              if (!saved) return;
            }
            await handleExecute();
          }}
          disabled={isExecuted || submitting || !isExecuteValid()}
        >
          {isExecuted ? 'Ejecutado' : (submitting ? 'Ejecutando...' : 'Confirmar ejecución')}
        </Button>
      </>
    );
  };

  return (
    <Card variant='neutral' modifier='inverted' spacing={'4'} height='-webkit-fill-available' width='85vw'>
      {/* Header with back button and title */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver"
        />
        <H withMargin={false} variant="secondary-2" type='2' weight='bold' modifier='strong'>
          {getTitle()}
        </H>
      </Card>

      {/* General Section */}
      <GeneralSection 
        formData={formData}
        formValues={formValues}
        updateFormValues={updateFormValues}
      />

      {/* Conditional Sections based on operation type */}
      {renderConditionalSection()}

      {/* Observation Section */}
      <ObservationSection 
        formData={formData}
        formValues={formValues}
        updateFormValues={updateFormValues}
      />

      {/* Execution Section - Only for edit mode */}
      {mode === 'edit' && renderExecutionSection()}

      {/* Error Message */}
      {error && (
        <Card margin={{ top: '3' }} variant='danger' modifier='regular' spacing='3'>
          <P variant='danger' type='2' weight='regular'>
            {error}
          </P>
        </Card>
      )}

      {/* Action Buttons */}
      <Card margin={{ top: '4' }} display='flex' justifyContent='flex-end' alignItems='center' gap='32px'>
        {/* Validation Error Message */}
        {validationError && (
          <Card display='flex' alignItems='center'>
            <P variant='danger' type='3' withMargin={false}>
              {validationError}
            </P>
          </Card>
        )}
        
        {/* Buttons */}
        {renderActionButtons()}
      </Card>
    </Card>
  );
};

export default MonetizationForm;
